import * as React from 'react';
import { Upload, File, X, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { Text, Icon } from '../../display';
import { Button } from '../../actions';
import { InputLabel, InputMessage } from '../shared';

/**
 * Represents a file that has been uploaded or is being uploaded
 */
export interface UploadedFile {
  /**
   * The file object
   */
  file: File;
  /**
   * Object URL for image preview (only for image files)
   */
  preview?: string;
  /**
   * Upload progress from 0-100 (for external upload handling)
   */
  progress?: number;
  /**
   * Error message if file validation failed
   */
  error?: string;
}

/**
 * FileUpload component props
 */
export interface FileUploadProps {
  /**
   * Currently selected files (controlled mode)
   */
  value?: File[];
  /**
   * Callback when files are added or removed
   */
  onChange?: (files: File[]) => void;
  /**
   * Label text displayed above the dropzone
   */
  label?: string;
  /**
   * Helper text displayed below the dropzone
   */
  helperText?: string;
  /**
   * Whether the component is in an error state
   */
  error?: boolean;
  /**
   * Error message displayed when error is true
   */
  errorMessage?: string;
  /**
   * Accepted file types (MIME types or extensions)
   * Examples: "image/*", ".pdf,.doc", "image/png,image/jpeg"
   */
  accept?: string;
  /**
   * Maximum file size in bytes
   */
  maxSize?: number;
  /**
   * Maximum number of files allowed
   */
  maxFiles?: number;
  /**
   * Whether multiple files can be selected
   * @default false
   */
  multiple?: boolean;
  /**
   * Whether the component is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Whether to show image previews
   * @default false
   */
  showPreview?: boolean;
  /**
   * Callback for upload progress (for external upload handling)
   */
  onUploadProgress?: (file: File, progress: number) => void;
  /**
   * Whether the field is required
   * @default false
   */
  required?: boolean;
  /**
   * Additional CSS classes for the container
   */
  className?: string;
  /**
   * ID for the file input (for accessibility)
   */
  id?: string;
  /**
   * Main dropzone text, replacing the default
   * "Drag & drop files here, or click to browse" (e.g. a translated prompt).
   */
  prompt?: React.ReactNode;
  /**
   * Hint line under the prompt. Defaults to the accepted types / max size
   * hints; `null` hides the line.
   */
  hint?: React.ReactNode | null;
  /**
   * Accessible name of the dropzone button. Defaults to
   * "Upload files. <accepted types / max size hints>".
   */
  dropzoneLabel?: string;
  /**
   * Compact dropzone (smaller height, padding and icon) for inline use,
   * e.g. next to a list item.
   * @default false
   */
  compact?: boolean;
}

/**
 * Format bytes to human readable string
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Check if a single pattern matches the file
 */
function matchesSinglePattern(
  pattern: string,
  fileType: string,
  fileExtension: string
): boolean {
  // Match MIME type wildcards (e.g., "image/*")
  if (pattern.endsWith('/*')) {
    const baseType = pattern.replace('/*', '');
    return fileType.startsWith(baseType + '/');
  }
  // Match exact MIME type
  if (pattern.includes('/')) {
    return fileType === pattern;
  }
  // Match file extension
  if (pattern.startsWith('.')) {
    return fileExtension === pattern;
  }
  return false;
}

/**
 * Check if a file type matches an accept pattern
 */
function matchesAccept(file: File, accept: string): boolean {
  /* v8 ignore start */
  if (!accept) return true;
  /* v8 ignore stop */

  const acceptPatterns = accept.split(',').map((p) => p.trim().toLowerCase());
  const fileType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();
  const fileExtension = '.' + fileName.split('.').pop();

  return acceptPatterns.some((pattern) =>
    matchesSinglePattern(pattern, fileType, fileExtension)
  );
}

/**
 * Base classes for the dropzone
 */
const dropzoneBaseClasses = [
  'relative',
  'flex',
  'flex-col',
  'items-center',
  'justify-center',
  'w-full',
  'border-2',
  'border-dashed',
  'rounded-lg',
  'cursor-pointer',
  'transition-all',
  'duration-150',
  'ease-out',
  'outline-none',
  'focus-visible:ring-2',
  'focus-visible:ring-primary-200',
  'focus-visible:border-primary-400',
].join(' ');

/**
 * Dropzone sizing (height + padding), default or compact
 */
const dropzoneSizeClasses = {
  default: 'min-h-[160px] px-6 py-8',
  compact: 'min-h-[96px] px-4 py-4',
} as const;

/**
 * Get dropzone state classes based on isDragOver, error, and disabled states
 */
function getDropzoneStateClasses(
  isDragOver: boolean,
  error: boolean,
  disabled: boolean
): string {
  if (disabled) {
    return 'bg-secondary-100 border-secondary-300 cursor-not-allowed opacity-50';
  }
  if (isDragOver) {
    return 'border-primary-400 bg-primary-50';
  }
  if (error) {
    return 'border-error-700';
  }
  return 'border-secondary-300 hover:border-primary-400 hover:bg-primary-50';
}

/**
 * Check if file is an image for preview
 */
function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Get file item container classes based on error state
 */
function getFileItemClasses(hasError: boolean): string {
  const baseClasses = 'flex items-center gap-3 p-3 rounded-lg border';
  if (hasError) {
    return `${baseClasses} border-error-700 bg-error-50`;
  }
  return `${baseClasses} border-secondary-200 bg-secondary-50`;
}

/**
 * FileUpload component with drag-and-drop support, file validation, and preview capabilities.
 *
 * @example
 * // Basic usage
 * <FileUpload
 *   label="Upload Documents"
 *   onChange={(files) => console.log(files)}
 * />
 *
 * @example
 * // Image upload with preview
 * <FileUpload
 *   label="Upload Images"
 *   accept="image/*"
 *   showPreview
 *   multiple
 *   maxFiles={5}
 *   maxSize={5 * 1024 * 1024}
 * />
 *
 * @example
 * // PDF upload only
 * <FileUpload
 *   label="Upload PDF"
 *   accept=".pdf"
 *   helperText="Only PDF files are accepted"
 * />
 */
const FileUpload = React.forwardRef<HTMLInputElement, FileUploadProps>(
  (
    {
      value,
      onChange,
      label,
      helperText,
      error = false,
      errorMessage,
      accept,
      maxSize,
      maxFiles = 1,
      multiple = false,
      disabled = false,
      showPreview = false,
      onUploadProgress: _onUploadProgress, // Reserved for external upload handling
      required = false,
      className = '',
      id: providedId,
      prompt,
      hint,
      dropzoneLabel,
      compact = false,
    },
    ref
  ) => {
    // Generate stable IDs for accessibility
    const reactId = React.useId();
    const inputId = providedId || `fileupload-${reactId}`;
    const helperId = `fileupload-helper-${reactId}`;

    // Internal file state for uncontrolled mode
    const [internalFiles, setInternalFiles] = React.useState<UploadedFile[]>([]);
    const [isDragOver, setIsDragOver] = React.useState(false);
    const [validationErrors, setValidationErrors] = React.useState<
      Map<string, string>
    >(new Map());

    // Determine if controlled or uncontrolled
    const isControlled = value !== undefined;
    const files: UploadedFile[] = isControlled
      ? value.map((f) => ({ file: f } as UploadedFile))
      : internalFiles;

    // File input ref
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // Merge refs
    React.useImperativeHandle(ref, () => fileInputRef.current!, []);

    // Track current files via ref so cleanup effect captures latest value
    const filesRef = React.useRef(internalFiles);
    filesRef.current = internalFiles;

    // Clean up preview URLs when component unmounts
    React.useEffect(() => {
      return () => {
        /* v8 ignore start */
        filesRef.current.forEach((f) => {
          if (f.preview) {
            URL.revokeObjectURL(f.preview);
          }
        });
        /* v8 ignore stop */
      };
    }, []);

    /**
     * Validate a file against accept, maxSize constraints
     */
    const validateFile = (file: File): string | null => {
      // Check accept pattern
      if (accept && !matchesAccept(file, accept)) {
        return `File type not accepted. Allowed: ${accept}`;
      }

      // Check max size
      if (maxSize && file.size > maxSize) {
        return `File size exceeds ${formatFileSize(maxSize)}`;
      }

      return null;
    };

    /**
     * Process and add files
     */
    const processFiles = (newFiles: FileList | File[]) => {
      const fileArray = Array.from(newFiles);
      const currentCount = files.length;
      const effectiveMaxFiles = multiple ? maxFiles : 1;
      const availableSlots = effectiveMaxFiles - currentCount;

      if (availableSlots <= 0) {
        setValidationErrors(
          new Map([['limit', `Maximum ${effectiveMaxFiles} file(s) allowed`]])
        );
        return;
      }

      // Process only up to available slots
      const filesToProcess = fileArray.slice(0, availableSlots);
      const newUploadedFiles: UploadedFile[] = [];
      const newErrors = new Map<string, string>();

      filesToProcess.forEach((file) => {
        const validationError = validateFile(file);
        if (validationError) {
          newErrors.set(file.name, validationError);
          newUploadedFiles.push({
            file,
            error: validationError,
          });
        } else {
          // Create preview for images
          let preview: string | undefined;
          if (showPreview && file.type.startsWith('image/')) {
            preview = URL.createObjectURL(file);
          }
          newUploadedFiles.push({
            file,
            preview,
          });
        }
      });

      if (fileArray.length > availableSlots) {
        newErrors.set(
          'limit',
          `Only ${availableSlots} more file(s) can be added. ${
            fileArray.length - availableSlots
          } file(s) were not added.`
        );
      }

      setValidationErrors(newErrors);

      if (isControlled) {
        // In controlled mode, only pass valid files
        const validFiles = newUploadedFiles
          .filter((f) => !f.error)
          .map((f) => f.file);
        onChange?.([...value, ...validFiles]);
      } else {
        // In uncontrolled mode, store all files including errored ones
        const combined = [...internalFiles, ...newUploadedFiles];
        setInternalFiles(combined);
        const validFiles = combined.filter((f) => !f.error).map((f) => f.file);
        onChange?.(validFiles);
      }
    };

    /**
     * Remove a file at the specified index
     */
    const removeFile = (index: number) => {
      if (isControlled) {
        const newFiles = [...value];
        newFiles.splice(index, 1);
        onChange?.(newFiles);
      } else {
        const fileToRemove = internalFiles[index];
        if (fileToRemove?.preview) {
          URL.revokeObjectURL(fileToRemove.preview);
        }
        const newFiles = [...internalFiles];
        newFiles.splice(index, 1);
        setInternalFiles(newFiles);
        /* v8 ignore start -- filter/map callbacks never execute when array is empty after removing the only file */
        const validFiles = newFiles.filter((f) => !f.error).map((f) => f.file);
        /* v8 ignore stop */
        onChange?.(validFiles);
      }

      // Clear any validation errors when a file is removed
      setValidationErrors(new Map());
    };

    /**
     * Handle file input change
     */
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(e.target.files);
      }
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    };

    /**
     * Handle click on dropzone
     */
    const handleDropzoneClick = () => {
      /* v8 ignore start */
      if (!disabled) {
        /* v8 ignore stop */
        fileInputRef.current?.click();
      }
    };

    /**
     * Handle keyboard interaction on dropzone
     */
    const handleDropzoneKeyDown = (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
        e.preventDefault();
        fileInputRef.current?.click();
      }
    };

    /**
     * Handle drag over
     */
    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragOver(true);
      }
    };

    /**
     * Handle drag leave
     */
    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
    };

    /**
     * Handle drop
     */
    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (disabled) return;

      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles && droppedFiles.length > 0) {
        processFiles(droppedFiles);
      }
    };

    // Determine message ID for aria-describedby
    const showError = error && errorMessage;
    const messageId = (showError || helperText) ? helperId : undefined;

    // Build accept hint text
    const getAcceptHint = (): string => {
      if (!accept) return 'All file types accepted';

      const parts = accept.split(',').map((p) => p.trim());
      const readableParts = parts.map((p) => {
        if (p === 'image/*') return 'images';
        if (p === 'video/*') return 'videos';
        if (p === 'audio/*') return 'audio files';
        if (p === 'application/pdf' || p === '.pdf') return 'PDF';
        if (p.startsWith('.')) return p.toUpperCase().slice(1);
        return p;
      });

      return readableParts.join(', ');
    };

    // Build size hint text
    const getSizeHint = (): string | null => {
      if (!maxSize) return null;
      return `Max size: ${formatFileSize(maxSize)}`;
    };

    const acceptHint = getAcceptHint();
    const sizeHint = getSizeHint();
    const hints = [acceptHint, sizeHint].filter(Boolean).join(' | ');
    // `hint` replaces the computed hints; `null` hides the line.
    const hintContent = hint === undefined ? hints : hint;

    // Dropzone classes - computed using helper functions
    const dropzoneClasses = [
      dropzoneBaseClasses,
      dropzoneSizeClasses[compact ? 'compact' : 'default'],
      getDropzoneStateClasses(isDragOver, error, disabled),
    ].join(' ');

    return (
      <div className={`w-full ${className}`}>
        {/* Label */}
        {label && <InputLabel label={label} htmlFor={inputId} required={required} />}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          id={inputId}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={handleInputChange}
          className="sr-only"
          aria-describedby={messageId}
          aria-invalid={error}
          aria-required={required}
        />

        {/* Dropzone */}
        <button
          type="button"
          tabIndex={disabled ? -1 : 0}
          className={`${dropzoneClasses} bg-transparent text-left w-full`}
          onClick={handleDropzoneClick}
          onKeyDown={handleDropzoneKeyDown}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          disabled={disabled}
          aria-label={dropzoneLabel ?? `Upload files. ${hints}`}
        >
          <Icon
            icon={Upload}
            size={compact ? 'md' : 'xl'}
            color={isDragOver ? 'primary' : 'muted'}
            className={compact ? 'mb-2' : 'mb-3'}
          />
          <Text size="sm" weight="medium" color={disabled ? 'muted' : 'default'}>
            {prompt ?? 'Drag & drop files here, or click to browse'}
          </Text>
          {hintContent !== null && hintContent !== '' && (
            <Text size="xs" color="muted" className="mt-1">
              {hintContent}
            </Text>
          )}
          {multiple && maxFiles > 1 && (
            <Text size="xs" color="muted" className="mt-1">
              Up to {maxFiles} files
            </Text>
          )}
        </button>

        {/* Validation errors */}
        {validationErrors.size > 0 && (
          <div className="mt-2 space-y-1">
            {Array.from(validationErrors.entries()).map(([key, error]) => (
              <div key={key} className="flex items-center gap-1.5">
                <Icon icon={AlertCircle} size="sm" color="error" />
                <Text size="xs" color="error">
                  {key === 'limit' ? '' : `${key}: `}
                  {error}
                </Text>
              </div>
            ))}
          </div>
        )}

        {/* Selected files list */}
        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            {files.map((uploadedFile, index) => (
              <div
                key={`${uploadedFile.file.name}-${index}`}
                className={getFileItemClasses(!!uploadedFile.error)}
              >
                {/* Preview or file icon */}
                <div className="flex-shrink-0">
                  {showPreview &&
                  uploadedFile.preview &&
                  isImageFile(uploadedFile.file) ? (
                    <img
                      src={uploadedFile.preview}
                      alt={`Preview of ${uploadedFile.file.name}`}
                      className="w-12 h-12 object-cover rounded border border-secondary-200"
                    />
                  ) : (
                    <div className="w-12 h-12 flex items-center justify-center rounded bg-secondary-100 border border-secondary-200">
                      <Icon
                        icon={
                          isImageFile(uploadedFile.file) ? ImageIcon : File
                        }
                        size="lg"
                        color="muted"
                      />
                    </div>
                  )}
                </div>

                {/* File info */}
                <div className="flex-1 min-w-0">
                  <Text
                    size="sm"
                    weight="medium"
                    truncate
                    color={uploadedFile.error ? 'error' : 'default'}
                  >
                    {uploadedFile.file.name}
                  </Text>
                  <Text size="xs" color="muted">
                    {formatFileSize(uploadedFile.file.size)}
                  </Text>
                  {uploadedFile.error && (
                    <Text size="xs" color="error" className="mt-0.5">
                      {uploadedFile.error}
                    </Text>
                  )}
                  {/* Progress bar */}
                  {/* v8 ignore start */}
                  {uploadedFile.progress !== undefined &&
                    uploadedFile.progress < 100 && (
                      <div className="mt-1.5">
                        <div className="w-full h-1 bg-secondary-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary-600 transition-all duration-150"
                            style={{ width: `${uploadedFile.progress}%` }}
                          />
                        </div>
                        <Text size="xs" color="muted" className="mt-0.5">
                          {uploadedFile.progress}% uploaded
                        </Text>
                      </div>
                    )}
                  {/* v8 ignore stop */}
                </div>

                {/* Remove button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(index);
                  }}
                  disabled={disabled}
                  aria-label={`Remove ${uploadedFile.file.name}`}
                  leftIcon={<Icon icon={X} size="sm" />}
                />
              </div>
            ))}
          </div>
        )}

        {/* Helper text or error message */}
        <InputMessage error={error} errorMessage={errorMessage} helperText={helperText} helperId={helperId} />
      </div>
    );
  }
);

FileUpload.displayName = 'FileUpload';

export { FileUpload };
